import { Button } from "@/components/ui/button";
import { Check, X, Phone, Mail, Globe, Zap, Clock, ArrowRight, Timer, Rocket, Target, TrendingUp, Users, Star, ChevronRight } from "lucide-react";

const Homepage3SecondAdvantage = () => {
  const steps = [
    { time: "0s", action: "Tap", desc: "They tap your QCard link" },
    { time: "1s", action: "View", desc: "Your profile loads instantly" },
    { time: "2s", action: "Save", desc: "One tap → Add to Contacts" },
    { time: "3s", action: "Done", desc: "You're in their phone forever" },
  ];

  const frictionPoints = [
    { method: "Paper Cards", time: "Never saved", friction: "Lost, forgotten, tossed" },
    { method: "Typing Numbers", time: "45+ seconds", friction: "Errors, interruptions" },
    { method: "QR Codes", time: "15+ seconds", friction: "App switching, friction" },
    { method: "QCard", time: "3 seconds", friction: "Zero friction", highlight: true },
  ];

  const benefits = [
    { icon: Zap, title: "Zero Friction", desc: "No typing, no scanning, no apps" },
    { icon: Target, title: "100% Accuracy", desc: "No typos or wrong numbers" },
    { icon: TrendingUp, title: "Higher Follow-Through", desc: "Easy = more saves = more calls" },
    { icon: Users, title: "More Opportunities", desc: "Every connection counts" },
  ];

  const testimonials = [
    { name: "Michael Torres", role: "Tech Entrepreneur", quote: "In the time it takes to spell my last name, they've already saved my QCard. Game changer." },
    { name: "Jennifer Lee", role: "Sales Manager", quote: "My team's follow-up rate jumped 40% after switching to QCard. Speed matters." },
    { name: "Robert Chen", role: "Consultant", quote: "I used to lose deals to friction. Now I close faster because I'm already in their contacts." },
  ];

  const comparisons = [
    { feature: "Time to Save Contact", paper: "Never", qcard: "3 seconds" },
    { feature: "Requires Typing", paper: "Yes", qcard: "No" },
    { feature: "Error Rate", paper: "High", qcard: "Zero" },
    { feature: "Photo Included", paper: "No", qcard: "Yes" },
    { feature: "Always Current", paper: "No", qcard: "Yes" },
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] text-gray-900">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-white via-blue-50 to-cyan-50">
        {/* Speed lines background */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute h-0.5 bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent"
              style={{
                top: `${10 + i * 12}%`,
                left: '-10%',
                right: '-10%',
                transform: `rotate(-${2 + i * 0.5}deg)`,
              }}
            />
          ))}
        </div>
        
        <div className="container mx-auto px-4 py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-600">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-semibold">The 3-Second Advantage</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight text-gray-900">
                It takes
                <span className="block text-cyan-500">three seconds</span>
                to turn a moment into a connection.
              </h1>
              
              <p className="text-xl text-gray-600 max-w-lg">
                Networking breaks down because exchanging info takes too long. QCard eliminates all friction. Tap → Save → Done.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-6 text-lg rounded-full shadow-lg shadow-cyan-500/25">
                  <Zap className="w-5 h-5 mr-2" />
                  Get Your Free QCard
                </Button>
                <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                  Watch Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
            
            {/* Timer Visual */}
            <div className="relative flex justify-center">
              <div className="relative">
                {/* Central timer display */}
                <div className="w-80 h-80 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 p-1 shadow-2xl shadow-cyan-500/30">
                  <div className="w-full h-full rounded-full bg-white flex flex-col items-center justify-center">
                    <Timer className="w-12 h-12 text-cyan-500 mb-2" />
                    <div className="text-7xl font-bold bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">3</div>
                    <div className="text-xl text-gray-500 font-medium">seconds</div>
                    <div className="mt-4 text-sm text-gray-400">Tap → Save → Done</div>
                  </div>
                </div>
                
                {/* Floating step cards */}
                {steps.map((step, index) => {
                  const positions = [
                    { top: '-20px', left: '-80px' },
                    { top: '-20px', right: '-80px' },
                    { bottom: '-20px', left: '-80px' },
                    { bottom: '-20px', right: '-80px' },
                  ];
                  return (
                    <div
                      key={index}
                      className="absolute w-28 bg-white rounded-xl shadow-lg p-3 text-center border border-gray-100"
                      style={positions[index]}
                    >
                      <div className="text-xs text-cyan-500 font-bold">{step.time}</div>
                      <div className="font-semibold text-gray-900">{step.action}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem: Friction Kills */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Friction Kills Connections</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              People skip saving you because it takes too long. The friction destroys connection before it starts.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-4">
              {frictionPoints.map((item, index) => (
                <div
                  key={index}
                  className={`p-6 rounded-2xl flex items-center justify-between ${
                    item.highlight
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/25'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      item.highlight ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {item.highlight ? <Zap className="w-6 h-6" /> : <Clock className="w-6 h-6 text-gray-500" />}
                    </div>
                    <div>
                      <div className="font-bold text-lg">{item.method}</div>
                      <div className={item.highlight ? 'text-white/80' : 'text-gray-500'}>{item.friction}</div>
                    </div>
                  </div>
                  <div className={`text-2xl font-bold ${item.highlight ? '' : 'text-gray-400'}`}>
                    {item.time}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8 text-gray-900">The Cost of Slow</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="text-5xl font-bold text-red-500 mb-2">90%</div>
                <p className="text-gray-600">of contacts never get saved</p>
              </div>
              <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="text-5xl font-bold text-orange-500 mb-2">$$$</div>
                <p className="text-gray-600">lost in missed opportunities</p>
              </div>
              <div className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="text-5xl font-bold text-cyan-500 mb-2">3s</div>
                <p className="text-gray-600">is all it takes to fix</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                QCard Eliminates
                <span className="text-cyan-500"> All Friction</span>
              </h2>
              <p className="text-xl text-gray-600">
                Your profile — complete with photo, job title, links, and keywords — is saved in under three seconds. No typing. No scanning. No apps.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-cyan-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{benefit.title}</h3>
                      <p className="text-gray-500 text-sm">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-8 py-6 text-lg rounded-full">
                Get Your Free QCard
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
            
            {/* Phone Demo */}
            <div className="relative flex justify-center">
              <div className="relative w-72 h-[580px] bg-gray-900 rounded-[3rem] border-4 border-gray-800 shadow-2xl">
                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full" />
                <div className="absolute inset-4 top-12 bg-white rounded-[2rem] overflow-hidden">
                  <div className="p-6 space-y-4">
                    {/* Speed indicator */}
                    <div className="flex items-center justify-center gap-2 py-2 bg-cyan-50 rounded-xl">
                      <Zap className="w-5 h-5 text-cyan-500" />
                      <span className="text-cyan-600 font-semibold">Ready in 3 seconds</span>
                    </div>
                    
                    {/* Profile */}
                    <div className="text-center space-y-3 pt-2">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                        JD
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">John Doe</h3>
                        <p className="text-cyan-500 font-medium">Product Manager</p>
                        <p className="text-gray-400 text-sm">TechStartup Inc.</p>
                      </div>
                    </div>
                    
                    {/* Contact details */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <Phone className="w-5 h-5 text-cyan-500" />
                        <span className="text-gray-700">+1 (555) 123-4567</span>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                        <Mail className="w-5 h-5 text-cyan-500" />
                        <span className="text-gray-700">john@startup.com</span>
                      </div>
                    </div>
                    
                    {/* CTA */}
                    <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white py-6 rounded-xl text-lg">
                      <Zap className="w-5 h-5 mr-2" />
                      Add to Contacts
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Speed Comparison</h2>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="grid grid-cols-3 gap-4 p-6 bg-gray-50 font-bold">
                <div className="text-gray-600">Feature</div>
                <div className="text-center text-gray-400">Paper Card</div>
                <div className="text-center text-cyan-500">QCard</div>
              </div>
              {comparisons.map((item, index) => (
                <div key={index} className="grid grid-cols-3 gap-4 p-6 border-t border-gray-100">
                  <div className="text-gray-700 font-medium">{item.feature}</div>
                  <div className="text-center text-gray-400">{item.paper}</div>
                  <div className="text-center text-cyan-500 font-semibold">{item.qcard}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Free Tier */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Rocket className="w-16 h-16 mx-auto text-cyan-500 mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Fast, Free, Forever</h2>
            <p className="text-xl text-gray-600 mb-12">
              Get your professional QCard in 60 seconds. No credit card. No hidden fees.
            </p>
            
            <div className="grid md:grid-cols-4 gap-4">
              {["Instant Profile", "Add to Contacts", "Unlimited Shares", "Always Free"].map((item, index) => (
                <div key={index} className="p-6 bg-cyan-50 rounded-2xl">
                  <Check className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
                  <p className="font-semibold text-gray-900">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">Speed Wins</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-8 bg-white rounded-2xl shadow-sm border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-cyan-400 fill-cyan-400" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-r from-cyan-500 to-blue-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              3 Seconds to Better Networking
            </h2>
            <p className="text-xl text-white/80 mb-12">
              Zero friction = higher follow-through = more opportunities = more business.
            </p>
            <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100 px-12 py-8 text-xl rounded-full shadow-lg">
              <Zap className="w-6 h-6 mr-2" />
              Get Your Free QCard Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center text-gray-400">
          <p>© 2024 QCard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Homepage3SecondAdvantage;